<template>
  <div class="recent-activity card border shadow-sm">
    <div class="recent-activity__head d-flex align-items-center justify-content-between">
      <h6 class="recent-activity__title mb-0">
        {{ $t("recentActivity.title") }}
      </h6>
      <button
        type="button"
        class="btn btn-sm btn-link p-0 text-secondary"
        :title="$t('recentActivity.reload')"
        :disabled="loading"
        @click="load"
      >
        <i class="material-icons" :class="{ 'recent-activity__spin': loading }">refresh</i>
      </button>
    </div>

    <div class="recent-activity__body">
      <ContentSkeleton v-if="loading && !items.length" variant="list" :rows="4" />
      <p v-else-if="error" class="recent-activity__empty mb-0">
        {{ $t("recentActivity.error") }}
      </p>
      <p v-else-if="!items.length" class="recent-activity__empty mb-0">
        {{ $t("recentActivity.empty") }}
      </p>
      <ul v-else class="recent-activity__list">
        <li v-for="item in items" :key="item.modelName + item.id" class="recent-activity__item">
          <router-link class="recent-activity__link" :to="itemPath(item)">{{ item.label }}</router-link>
          <span class="recent-activity__text">
            {{ createdText(item) }}<span v-if="modifiedText(item)"> {{ modifiedText(item) }}</span>
          </span>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
/**
 * Actividad reciente del tenant: los últimos documentos tocados, con quién los creó
 * y quién los modificó por última vez. Una sola llamada acotada al backend
 * (/api/gotrading/dynamic/recent-activity); el dashboard ya hace bastantes.
 */
import { wencesApi } from "@/api/wencesApi";
import { buildDynamicItemPrettySlug } from "@/utils/dynamicModelPaths";
import { formatTimestampSync } from "@/utils/dateFormatter";
import ContentSkeleton from "@/components/common/ContentSkeleton.vue";

export default {
  name: "RecentActivity",
  components: { ContentSkeleton },
  props: {
    /** Cuántas líneas mostrar. */
    limit: { type: Number, default: 12 },
  },
  data() {
    return {
      items: [],
      loading: false,
      error: false,
    };
  },
  mounted() {
    this.load();
  },
  methods: {
    async load() {
      this.loading = true;
      this.error = false;
      try {
        const { data } = await wencesApi.get("/api/gotrading/dynamic/recent-activity", {
          params: { limit: this.limit },
        });
        this.items = Array.isArray(data?.data) ? data.data : [];
      } catch (e) {
        this.error = true;
        this.items = [];
      } finally {
        this.loading = false;
      }
    },
    itemPath(item) {
      return `/i/${buildDynamicItemPrettySlug(item.modelName, item.id, item.label)}`;
    },
    /** «was created by X at 06/07/2026 - 11:49» */
    createdText(item) {
      const when = formatTimestampSync(item.createdAt);
      const who = item.createdByName;
      if (who && when) {
        return this.$t("recentActivity.createdByAt", { who, when });
      }
      if (when) return this.$t("recentActivity.createdAt", { when });
      if (who) return this.$t("recentActivity.createdBy", { who });
      return "";
    },
    /** «(last modified by Y at 07/09/2026 - 12:51)» — vacío si nunca se modificó. */
    modifiedText(item) {
      const when = formatTimestampSync(item.updatedAt);
      if (!when) return "";
      // Sin cambios posteriores al alta no hay nada que agregar.
      if (item.createdAt && item.updatedAt && item.createdAt === item.updatedAt) return "";
      const who = item.updatedByName;
      if (who) return this.$t("recentActivity.modifiedByAt", { who, when });
      return this.$t("recentActivity.modifiedAt", { when });
    },
  },
};
</script>

<style scoped>
.recent-activity {
  border-radius: 0.4rem;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.recent-activity__head {
  padding: 0.55rem 0.85rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  background: #f8f9fa;
}

.recent-activity__title {
  /* El dashboard pone los h6 en mayúsculas; acá se quiere «Recent activity». */
  text-transform: none;
  font-size: 0.95rem;
  font-weight: 600;
  letter-spacing: 0.01em;
  color: #37474f;
}

/* El alto lo pone el contenedor (en el dashboard, el del calendario). */
.recent-activity__body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 0.35rem 0.85rem 0.6rem;
}

.recent-activity__list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.recent-activity__item {
  padding: 0.42rem 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  font-size: 0.82rem;
  line-height: 1.45;
  color: #546e7a;
}

.recent-activity__item:last-child {
  border-bottom: 0;
}

.recent-activity__link {
  font-weight: 600;
  margin-right: 0.25rem;
}

.recent-activity__text {
  word-break: break-word;
}

.recent-activity__empty {
  padding: 0.6rem 0;
  font-size: 0.82rem;
  color: #90a4ae;
}

.recent-activity__spin {
  animation: recent-activity-spin 0.9s linear infinite;
}

@keyframes recent-activity-spin {
  to {
    transform: rotate(360deg);
  }
}

@media (prefers-reduced-motion: reduce) {
  .recent-activity__spin {
    animation: none;
  }
}
</style>
